import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import NotificationsBell from './NotificationsBell';
import { 
  Car, 
  LayoutDashboard, 
  Users, 
  ShoppingCart, 
  CreditCard,
  BarChart3,
  LogOut, 
  Menu, 
  X,
  Package,
  DollarSign,
  Settings,
  FileText,
  Ship,
  Calendar,
  Calculator,
  Camera
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const Layout = ({ children, user, onLogout }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: LayoutDashboard,
      roles: ['admin', 'employee', 'customer']
    },
    {
      name: 'Vehicles',
      href: '/admin/vehicles',
      icon: Car,
      roles: ['admin', 'employee'],
      permission: 'vehicles_view'
    },
    {
      name: 'Orders',
      href: '/orders',
      icon: ShoppingCart,
      roles: ['admin', 'employee'],
      permission: 'orders_view'
    },
    {
      name: 'My Orders',
      href: '/my-orders',
      icon: Package,
      roles: ['admin', 'employee', 'customer']
    },
    {
      name: 'Payments',
      href: '/payments',
      icon: CreditCard,
      roles: ['admin', 'employee'],
      permission: 'payments_view'
    },
    {
      name: 'Commissions',
      href: '/commissions',
      icon: DollarSign,
      roles: ['admin', 'employee']
    },
    {
      name: 'Customer Inquiries',
      href: '/inquiries',
      icon: Package,
      roles: ['admin', 'employee'],
      permission: 'orders_view'
    },
    {
      name: 'Vehicle Requests',
      href: '/vehicle-requests',
      icon: Camera,
      roles: ['admin', 'employee']
    },
    {
      name: 'Reports',
      href: '/reports',
      icon: BarChart3,
      roles: ['admin', 'employee'],
      permission: 'reports_view'
    },
    {
      name: 'Blog Posts',
      href: '/blog-management',
      icon: FileText,
      roles: ['admin', 'employee']
    },
    {
      name: 'Shipping Rules',
      href: '/shipping-rules',
      icon: Ship,
      roles: ['admin']
    },
    {
      name: 'Agent Calculator',
      href: '/agent-calculator',
      icon: Calculator,
      roles: ['admin','employee','agent']
    },
    {
      name: 'Arrival Schedule',
      href: '/admin/arrival-schedule',
      icon: Calendar,
      roles: ['admin']
    },
    {
      name: 'Users',
      href: '/users',
      icon: Users,
      roles: ['admin'],
      permission: 'users_view'
    },
    {
      name: 'Company Settings',
      href: '/settings/company',
      icon: Settings,
      roles: ['admin']
    }
  ];

  const hasPermission = (item) => {
    // Admin always has all permissions
    if (user?.role === 'admin') return true;
    
    // Customer only has access to Dashboard and My Orders
    if (user?.role === 'customer') {
      return item.roles.includes('customer');
    }
    
    // Employee needs to check permissions
    if (user?.role === 'employee') {
      // Dashboard and My Orders don't need permission check
      if (!item.permission) return true;
      
      // Check if employee has the required permission
      return user?.permissions?.[item.permission] === true;
    }
    
    return false;
  };

  const filteredNavigation = navigation.filter(item => {
    // First check if user's role is allowed
    if (!item.roles.includes(user?.role)) return false;
    
    // Then check permissions
    return hasPermission(item);
  });

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800';
      case 'employee':
        return 'bg-blue-100 text-blue-800';
      case 'customer':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCurrentPageTitle = () => {
    const currentNav = navigation.find(item => item.href === location.pathname);
    return currentNav?.name || 'Dashboard';
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed top-0 left-0 bottom-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Sidebar Header */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-slate-200">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
              <Car className="h-5 w-5 text-white" />
            </div>
            <span className="text-lg font-bold text-slate-800">Vehicle CRM</span>
          </div>
          
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1 rounded-md text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* User Info */}
        <div className="px-6 py-4 border-b border-slate-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-slate-200 to-slate-300 rounded-full flex items-center justify-center">
              <span className="text-slate-600 font-medium text-sm">
                {user?.full_name?.charAt(0)?.toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">
                {user?.full_name}
              </p>
              <div className="flex items-center space-x-2 mt-1">
                <Badge className={`text-xs ${getRoleBadgeColor(user?.role)}`}>
                  {user?.role?.toUpperCase()}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {filteredNavigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`
                  group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200
                  ${
                    isActive
                      ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }
                `}
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                <item.icon className={`mr-3 h-5 w-5 transition-colors ${
                  isActive ? 'text-blue-700' : 'text-slate-400 group-hover:text-slate-600'
                }`} />
                {item.name}
              </Link>
            );
          })}
        </nav>

        {/* Sidebar Footer */}
        <div className="px-4 py-4 border-t border-slate-200">
          <Button
            onClick={onLogout}
            variant="ghost"
            className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50 transition-colors"
            data-testid="logout-button"
          >
            <LogOut className="mr-3 h-5 w-5" />
            Sign Out
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 min-w-0 flex flex-col lg:ml-0">
        {/* Top Bar */}
        <header className="bg-white shadow-sm border-b border-slate-200 lg:static">
          <div className="flex items-center justify-between px-4 py-3 sm:px-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                data-testid="mobile-menu-button"
              >
                <Menu className="h-5 w-5" />
              </button>
              
              <div>
                <h1 className="text-xl font-semibold text-slate-900">
                  {getCurrentPageTitle()}
                </h1>
                {user?.company && (
                  <p className="text-sm text-slate-500">{user.company}</p>
                )}
              </div>
            </div>
            {user?.role !== 'customer' && (
              <div className="flex items-center space-x-2">
                <NotificationsBell />
              </div>
            )}
          </div>
        </header>

        {/* Page Content - Optimized spacing */}
        <main className="flex-1 overflow-auto">
          <div className="h-full px-4 py-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
